import { motion } from "framer-motion";
import { ArrowRight, Droplets, Gauge } from "lucide-react";

const VARIANTS = [
  {
    name: "Basic Defender",
    specs: [
      "Aluminum base 45″ × 37″ × 2″",
      "BE 1″ water pump (WP-1020R)",
      "1 valved discharge · hand-rewind reel",
      "¾″ × 100 ft industrial hose",
      "42 gpm max flow · 37 psi max",
    ],
    image: "/assets/tote-defender-1.jpg",
  },
  {
    name: "Firefighter Defender",
    specs: [
      "Aluminum base 45″ × 37″ × 2″",
      "Waterax Versax 6 pump",
      "3 valved discharges · hand-rewind reel",
      "1″ × 100 ft booster hose",
      "105 gpm max flow · 120 psi max",
    ],
    image: "/assets/tote-defender-2.jpg",
  },
];

export default function ToteDefender() {
  return (
    <section id="tote" className="py-20 lg:py-28 bg-white border-t border-stone-200" data-testid="tote-section">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="max-w-2xl mb-14"
        >
          <div className="font-tech text-[11px] tracking-[0.3em] text-[#f97316] uppercase mb-4" data-testid="tote-kicker">
            Also available
          </div>
          <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl text-[#17150f]" data-testid="tote-headline">
            IBC Tote Defender
          </h2>
          <p className="mt-4 text-base text-stone-600 leading-relaxed" data-testid="tote-description">
            A pump system that mounts on top of a standard 500L or 1000L IBC tote — an instant fire-suppression
            unit or a handy water tank for the yard. Set it on the tote, strap it down, connect the suction hose
            and you're ready. Built for industrial sites, construction, mining, farms, acreages and cabins.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6 mb-10">
          {VARIANTS.map((v, i) => (
            <motion.div
              key={v.name}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              className="rounded-xl border border-stone-200 bg-[#faf9f7] overflow-hidden"
              data-testid={`tote-variant-${i}`}
            >
              <img src={v.image} alt={`IBC Tote Defender — ${v.name}`} className="w-full aspect-[3/2] object-cover" loading="lazy" />
              <div className="p-7">
                <h3 className="font-display font-semibold text-xl text-[#17150f] mb-4">{v.name}</h3>
                <ul className="space-y-2 mb-6">
                  {v.specs.map((s) => (
                    <li key={s} className="flex items-start gap-2.5 text-sm text-stone-600">
                      <Droplets size={14} className="text-[#f97316] shrink-0 mt-1" />
                      {s}
                    </li>
                  ))}
                </ul>
                <div className="flex items-center justify-between">
                  <span className="font-tech text-[10px] tracking-[0.15em] uppercase text-stone-400">Price on request</span>
                  <a
                    href={`mailto:sales@noxway.shop?subject=IBC%20Tote%20Defender%20Quote%20—%20${encodeURIComponent(v.name)}`}
                    data-testid={`tote-quote-${i}`}
                    className="inline-flex items-center gap-2 font-display font-bold text-xs px-5 py-2.5 rounded-md bg-[#17150f] text-white hover:bg-[#f97316] transition-colors"
                  >
                    REQUEST QUOTE
                    <ArrowRight size={13} />
                  </a>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="flex items-start gap-3 rounded-xl border border-stone-200 bg-white p-5"
          data-testid="tote-options"
        >
          <Gauge size={18} className="text-[#f97316] shrink-0 mt-0.5" />
          <p className="text-sm text-stone-600 leading-relaxed">
            <span className="font-semibold text-[#17150f]">Options:</span> 2″ or 3″ high-volume pumps, pressure
            washers, firefighting pumps, UHP systems, firefighting foam, and electric rewind reels. Ask us about
            the configuration that fits your site.
          </p>
        </motion.div>
      </div>
    </section>
  );
}
